/**
 * Editing a skill on its page (ADR-216 Amendment 1). The latest version opens
 * as a draft kept in the browser's edit buffer; Review shows the changes and
 * takes a note; Publish sends the draft as the next version through the same
 * route the MCP tools use, so the same checks and secret scan apply.
 *
 * The publish names the version the draft started from. If someone published
 * meanwhile (SYD-K017) the draft is carried onto their version file by file,
 * and a file both sides changed waits for the member to settle it.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, FilePlus, FileText, Pencil, Trash2 } from 'lucide-react';
import { LoadingState } from '@systemdox/ui';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { useTenant } from '@/contexts/TenantContext';
import { hasSpaceScope } from '@/lib/grants';
import { cn } from '@/lib/utils';
import { STALE_BASE, skillsApi, type SkillDetail } from '@/services/skillsApi';
import { SkillDiff } from './SkillCompare';
import { CodeEditor } from './SkillFileView';
import { discardDraft, draftDiffers, loadDraft, saveDraft, type SkillDraft } from './skillDraft';
import {
  ENTRY_FILE,
  carryDraft,
  compareFiles,
  defaultNote,
  isMarkdown,
  isValidSkillPath,
  type SkillFileText,
} from './skillFiles';

/** The API's refusal text: an RFC 9457 `detail`, or the older `message`. */
export function refusalText(err: unknown): { code?: string; text: string } {
  const data = (err as { response?: { data?: { code?: string; detail?: string; message?: string } } })?.response?.data;
  const text = data?.detail ?? data?.message ?? 'The publish failed. Try again.';
  return { code: data?.code ?? /^(SYD-K\d{3})/.exec(text)?.[1], text };
}

/** The file and line a refusal names (`scripts/run.sh line 3 …`), when it names one of ours. */
export function refusalWhere(text: string, paths: readonly string[]): { path?: string; line?: number } {
  const path = [...paths].sort((a, b) => b.length - a.length).find((p) => text.includes(p));
  const line = path ? Number(new RegExp(`${path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} line (\\d+)`).exec(text)?.[1]) : NaN;
  return { path, line: Number.isFinite(line) ? line : undefined };
}

const BUTTON =
  'flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800';
const PRIMARY = 'flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50';

export function SkillEditPage() {
  const { name = '' } = useParams();
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [skill, setSkill] = useState<SkillDetail | null>(null);
  const [base, setBase] = useState<{ version: number; files: SkillFileText[] } | null>(null);
  const [files, setFiles] = useState<SkillFileText[]>([]);
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState(ENTRY_FILE);
  const [preview, setPreview] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [saved, setSaved] = useState<SkillDraft | null>(null);
  const [conflicts, setConflicts] = useState<string[]>([]);
  const [theirs, setTheirs] = useState<{ version: number; from: SkillFileText[]; files: SkillFileText[] } | null>(null);
  const [refusal, setRefusal] = useState<{ text: string; path?: string; line?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newPath, setNewPath] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const ready = useRef(false);

  useEffect(() => {
    Promise.all([skillsApi.get(name), skillsApi.version(name, 'latest'), loadDraft(name)])
      .then(([detail, latest, draft]) => {
        setSkill(detail);
        setBase({ version: latest.version, files: latest.files.map(({ path, content }) => ({ path, content })) });
        setFiles(latest.files.map(({ path, content }) => ({ path, content })));
        if (draft && (draft.baseVersion !== latest.version || draftDiffers(draft, latest.files))) setSaved(draft);
        ready.current = true;
      })
      .catch(() => setError('This skill does not exist, or you cannot read its space.'));
  }, [name]);

  const paths = useMemo(() => files.map((f) => f.path), [files]);
  const changes = useMemo(() => (base ? compareFiles(base.files, files) : []), [base, files]);
  const changed = changes.some((c) => c.status !== 'unchanged');

  // Every change is kept in the browser, a moment after the last keystroke; an
  // edit undone back to the version leaves no draft behind. A found draft is
  // left alone until the member restores or discards it.
  useEffect(() => {
    if (!ready.current || !base || saved) return;
    const t = setTimeout(
      () => void (changed ? saveDraft(name, { baseVersion: base.version, files, note }) : discardDraft(name)),
      400,
    );
    return () => clearTimeout(t);
  }, [name, base, files, note, saved, changed]);
  const current = files.find((f) => f.path === selected) ?? files[0];

  if (error) return <p className="text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (!skill || !base) return <LoadingState />;
  if (!hasSpaceScope(tenant?.grants, skill.spaceId, 'build:write')) {
    return (
      <p className="text-sm text-slate-600 dark:text-slate-400" data-testid="skill-edit-denied">
        Editing this skill needs write access to its space.
      </p>
    );
  }

  function edit(path: string, content: string) {
    setFiles((all) => all.map((f) => (f.path === path ? { ...f, content } : f)));
    if (refusal?.path === path) setRefusal(null);
  }

  function pathProblem(path: string, except?: string): string | null {
    if (!isValidSkillPath(path)) return 'Use letters, digits, ".", "-" and "_", with "/" between folders.';
    if (files.some((f) => f.path !== except && f.path.toLowerCase() === path.toLowerCase())) return 'A file has that name.';
    return null;
  }

  function addFile(path: string) {
    if (pathProblem(path)) return;
    setFiles((all) => [...all, { path, content: '' }].sort((a, b) => a.path.localeCompare(b.path)));
    setSelected(path);
    setNewPath(null);
  }

  function rename(from: string, to: string) {
    if (from === ENTRY_FILE || pathProblem(to, from)) return;
    setFiles((all) => all.map((f) => (f.path === from ? { ...f, path: to } : f)).sort((a, b) => a.path.localeCompare(b.path)));
    setSelected(to);
    setRenaming(null);
  }

  function remove(path: string) {
    if (path === ENTRY_FILE) return;
    setFiles((all) => all.filter((f) => f.path !== path));
    setSelected(ENTRY_FILE);
  }

  function restoreDraft() {
    if (!saved) return;
    setFiles(saved.files);
    setNote(saved.note);
    // A draft from an older version keeps that base: publishing it then
    // meets SYD-K017 and is carried onto the latest like any other.
    setBase((b) => (b && saved.baseVersion !== b.version ? { ...b, version: saved.baseVersion } : b));
    setSaved(null);
  }

  async function dropDraft() {
    await discardDraft(name);
    setSaved(null);
  }

  async function carryOnto() {
    const [detail, latest] = await Promise.all([skillsApi.get(name), skillsApi.version(name, 'latest')]);
    // The draft's base may have been pruned (only 20 are kept): then every file
    // the two sides hold differently is the member's to settle.
    const was = await skillsApi.version(name, base!.version).then((v) => v.files, () => [] as SkillFileText[]);
    const latestFiles = latest.files.map(({ path, content }) => ({ path, content }));
    const carried = carryDraft(was, latestFiles, files);
    setSkill(detail);
    setTheirs({ version: latest.version, from: was, files: latestFiles });
    setBase({ version: latest.version, files: latestFiles });
    setFiles(carried.files);
    setConflicts(carried.conflicts);
    if (carried.conflicts.length) setSelected(carried.conflicts[0]);
    setReviewing(carried.conflicts.length === 0);
  }

  async function publish() {
    setBusy(true);
    setRefusal(null);
    try {
      const out = await skillsApi.publish(name, { files, note, baseVersion: base!.version });
      await discardDraft(name);
      navigate(`/docs/skills/${encodeURIComponent(name)}`, { state: { published: out.version } });
    } catch (err) {
      const { code, text } = refusalText(err);
      if (code === STALE_BASE) {
        await carryOnto();
      } else {
        const where = refusalWhere(text, paths);
        setRefusal({ text, ...where });
        if (where.path) setSelected(where.path);
        setReviewing(false);
      }
    } finally {
      setBusy(false);
    }
  }

  function settle(path: string, take: 'mine' | 'theirs') {
    if (take === 'theirs') {
      const t = theirs?.files.find((f) => f.path === path);
      setFiles((all) =>
        t
          ? all.some((f) => f.path === path)
            ? all.map((f) => (f.path === path ? t : f))
            : [...all, t].sort((a, b) => a.path.localeCompare(b.path))
          : all.filter((f) => f.path !== path),
      );
    }
    setConflicts((c) => c.filter((p) => p !== path));
  }

  const theirFile = theirs?.files.find((f) => f.path === current?.path);

  return (
    <div className="mx-auto max-w-6xl" data-testid="skill-editor">
      <Link
        to={`/docs/skills/${encodeURIComponent(name)}`}
        className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" /> {name}
      </Link>
      <div className="mb-4 flex flex-wrap items-baseline gap-3">
        <h2 className="font-mono text-lg font-semibold text-slate-900 dark:text-slate-100">Edit {name}</h2>
        <span className="text-xs text-slate-500">
          from version {base.version} · your draft is kept in this browser until you publish
        </span>
      </div>

      {saved && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm dark:border-blue-900 dark:bg-blue-950/40" data-testid="skill-draft-found">
          <span className="text-blue-900 dark:text-blue-200">
            You have an unpublished draft of this skill from version {saved.baseVersion}, saved{' '}
            {new Date(saved.updatedAt).toLocaleString()}.
          </span>
          <button type="button" className={PRIMARY} onClick={restoreDraft} data-testid="skill-draft-restore">
            Restore it
          </button>
          <button type="button" className={BUTTON} onClick={dropDraft} data-testid="skill-draft-discard">
            Discard it
          </button>
        </div>
      )}

      {theirs && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="skill-conflict">
          <p>
            Version {theirs.version} was published while you were editing. Your changes are now on top of it.
            {conflicts.length > 0
              ? ` ${conflicts.length} file(s) were changed on both sides: choose which text to keep for each before publishing.`
              : ' No file was changed on both sides.'}
          </p>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs font-medium">What version {theirs.version} changed</summary>
            <div className="mt-2">
              <SkillDiff from={theirs.from} to={theirs.files} />
            </div>
          </details>
        </div>
      )}

      {refusal && (
        <p className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200" data-testid="skill-refusal">
          Nothing was published. {refusal.text}
        </p>
      )}

      {reviewing ? (
        <div data-testid="skill-review">
          <h3 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
            Review changes from version {base.version}
          </h3>
          <SkillDiff from={base.files} to={files} />
          <label className="mt-6 block text-sm font-medium text-slate-700 dark:text-slate-300" htmlFor="skill-note">
            Change note <span className="font-normal text-slate-400">(optional, shown in the history)</span>
          </label>
          <input
            id="skill-note"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            data-testid="skill-note"
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
          />
          <div className="mt-4 flex gap-2">
            <button type="button" className={BUTTON} onClick={() => setReviewing(false)} data-testid="skill-back-to-edit">
              Back to editing
            </button>
            <button
              type="button"
              className={PRIMARY}
              disabled={busy || !changed || conflicts.length > 0}
              onClick={publish}
              data-testid="skill-publish"
            >
              Publish version {base.version + 1}
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Computers that installed this skill keep their copy until someone runs install-skill there again.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-[15rem_1fr]">
          <div>
            <ul className="space-y-0.5 text-sm" data-testid="skill-editor-files">
              {files.map((f) => {
                const status = changes.find((c) => c.path === f.path)?.status;
                return (
                  <li key={f.path} className="group flex items-center gap-1">
                    {renaming === f.path ? (
                      <PathInput
                        initial={f.path}
                        problem={(p) => pathProblem(p, f.path)}
                        onDone={(p) => (p ? rename(f.path, p) : setRenaming(null))}
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => setSelected(f.path)}
                          className={cn(
                            'flex min-w-0 flex-1 items-center gap-1.5 rounded px-2 py-1 text-left font-mono text-xs',
                            f.path === current?.path
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                              : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800',
                          )}
                        >
                          <FileText className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{f.path}</span>
                          {conflicts.includes(f.path) ? (
                            <span className="ml-auto text-[10px] text-amber-600">both changed</span>
                          ) : status === 'added' || status === 'changed' ? (
                            <span className="ml-auto text-[10px] text-blue-500">{status}</span>
                          ) : null}
                        </button>
                        {f.path !== ENTRY_FILE && (
                          <>
                            <button
                              type="button"
                              title={`Rename ${f.path}`}
                              onClick={() => setRenaming(f.path)}
                              className="hidden rounded p-1 text-slate-400 hover:text-slate-700 group-hover:block"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                            <button
                              type="button"
                              title={`Delete ${f.path}`}
                              data-testid={`skill-delete-file-${f.path}`}
                              onClick={() => remove(f.path)}
                              className="hidden rounded p-1 text-slate-400 hover:text-red-600 group-hover:block"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
            {changes
              .filter((c) => c.status === 'removed')
              .map((c) => (
                <p key={c.path} className="px-2 py-1 font-mono text-xs text-red-500 line-through">
                  {c.path}
                </p>
              ))}
            {newPath !== null ? (
              <PathInput initial="references/" problem={(p) => pathProblem(p)} onDone={(p) => (p ? addFile(p) : setNewPath(null))} />
            ) : (
              <button
                type="button"
                onClick={() => setNewPath('')}
                data-testid="skill-add-file"
                className="mt-2 flex items-center gap-1 px-2 text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              >
                <FilePlus className="h-3.5 w-3.5" /> Add a file
              </button>
            )}
          </div>

          <div className="min-w-0">
            {current && (
              <>
                <div className="mb-2 flex items-center gap-2">
                  <span className="mr-auto truncate font-mono text-xs text-slate-500">{current.path}</span>
                  {isMarkdown(current.path) && (
                    <button type="button" className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800" onClick={() => setPreview(!preview)} data-testid="skill-editor-preview">
                      {preview ? <Pencil className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      {preview ? 'Edit' : 'Preview'}
                    </button>
                  )}
                </div>
                {conflicts.includes(current.path) && (
                  <div className="mb-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200" data-testid="skill-settle">
                    <p className="mb-2">
                      Version {theirs?.version} also changed this file
                      {theirFile ? ':' : ' (it deleted it).'} below is your text; theirs is here.
                    </p>
                    {theirFile && (
                      <pre className="mb-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-white p-2 font-mono dark:bg-slate-900" data-testid="skill-settle-theirs">
                        {theirFile.content}
                      </pre>
                    )}
                    <div className="flex gap-2">
                      <button type="button" className={BUTTON} onClick={() => settle(current.path, 'mine')} data-testid="skill-settle-mine">
                        Keep mine
                      </button>
                      <button type="button" className={BUTTON} onClick={() => settle(current.path, 'theirs')} data-testid="skill-settle-theirs-take">
                        Use theirs
                      </button>
                    </div>
                  </div>
                )}
                {preview && isMarkdown(current.path) ? (
                  <div className="docs-page h-[32rem] overflow-auto rounded-lg border border-slate-200 p-4 dark:border-slate-800">
                    <article>
                      <MarkdownRenderer content={current.content} />
                    </article>
                  </div>
                ) : (
                  <CodeEditor
                    key={current.path}
                    path={current.path}
                    value={current.content}
                    onChange={(v) => edit(current.path, v)}
                    errorLine={refusal?.path === current.path ? refusal.line : undefined}
                  />
                )}
              </>
            )}
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                className={PRIMARY}
                disabled={!changed || conflicts.length > 0}
                onClick={() => {
                  if (!note.trim()) setNote(defaultNote(changes));
                  setReviewing(true);
                }}
                data-testid="skill-review-changes"
              >
                Review changes
              </button>
              <Link to={`/docs/skills/${encodeURIComponent(name)}`} className={BUTTON}>
                Close
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** A file name typed in place: Enter keeps it, Escape cancels. */
function PathInput({
  initial,
  problem,
  onDone,
}: {
  initial: string;
  problem: (path: string) => string | null;
  onDone: (path: string | null) => void;
}) {
  const [value, setValue] = useState(initial);
  const why = value && value !== initial ? problem(value) : null;
  return (
    <div className="flex-1 px-1 py-1">
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value.trim())}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !problem(value)) onDone(value);
          if (e.key === 'Escape') onDone(null);
        }}
        data-testid="skill-path-input"
        className="w-full rounded border border-slate-300 bg-white px-2 py-1 font-mono text-xs dark:border-slate-700 dark:bg-slate-900"
      />
      {why && <p className="mt-1 text-[10px] text-red-500">{why}</p>}
    </div>
  );
}
